import { useMutation, useQueryClient } from "react-query";
import axios, { AxiosError } from "axios";
import { useQuery, UseQueryOptions } from "react-query";
import { FetchCheckoutProps, CheckoutData, TEditCheckout } from "./types";
import { serializeQuery } from "@xtribe/shared/lib/text-transformations";
import {
  ICreateOrder,
  IRetryOrderPayment,
  THandleCheckoutTry,
} from "../useOrders/types";
import { Stripe } from "@stripe/stripe-js";
import { handlePaymentResult } from "../../lib/orders/payments";

function useEditCheckout() {
  const queryClient = useQueryClient();
  return useMutation(
    ({ id, ...props }: TEditCheckout) =>
      axios
        .post(`/api/v1/checkouts/${id}/edit`, props)
        .then((res) => res.data)
        .catch((error) => {
          if (error?.response?.data?.message) {
            throw new Error(error?.response?.data?.message);
          } else throw error;
        }),
    {
      onSuccess: (data, props) => queryClient.invalidateQueries("checkouts"),
    }
  );
}

async function fetchCheckout({
  id,
  ...props
}: FetchCheckoutProps): Promise<CheckoutData> {
  const query = serializeQuery({
    ...props,
  });
  try {
    const res = await axios.get(
      `/api/v1/checkouts/${id}/get${query ? "?" + query : ""}`
    );
    return res.data;
  } catch (error) {
    if (error?.response?.data?.message) {
      throw new Error(error?.response?.data?.message);
    } else throw error;
  }
}

function useCheckout<TData = CheckoutData>(
  props: FetchCheckoutProps,
  options?: UseQueryOptions<CheckoutData, AxiosError, TData>
) {
  return useQuery(
    ["checkout", { ...props }],
    () => fetchCheckout({ ...props }),
    options
  );
}

const handleCheckoutTry = ({
  checkoutId,
  stripe,
  type,
  ...props
}: THandleCheckoutTry) => {
  return axios
    .post(`/api/v1/checkouts/${checkoutId}/${type}`, props)
    .then(async (result) => {
      let paymentResult;
      const apiPaymentResult = result.data?.paymentResult
        ? { ...result.data?.paymentResult }
        : null;
      if (result.data?.paymentResult) {
        paymentResult = await handlePaymentResult({
          paymentMethod:
            props.paymentMethodId ??
            apiPaymentResult.payment_intent?.payment_method,
          result: result.data?.paymentResult,
          stripe,
        });
        if (apiPaymentResult?.payment_intent?.status === "requires_action") {
          const scaResult = await axios.post(
            `/api/v1/checkouts/${checkoutId}/retry-sca`,
            props
          );
          if (scaResult) {
            paymentResult = scaResult.data;
          }
        }
      }
      return { ...result.data, paymentResult };
    })
    .catch((error) => {
      if (error?.response?.data?.message) {
        throw new Error(error?.response?.data?.message);
      } else throw error;
    });
};

function useProcessCheckout() {
  const queryClient = useQueryClient();
  return useMutation(
    ({ ...props }: ICreateOrder & { stripe: Stripe }) =>
      handleCheckoutTry({ ...props, type: "process" }),
    {
      onSettled: (data, props) => {
        queryClient.invalidateQueries("licenses");
        queryClient.invalidateQueries("orders");
      },
    }
  );
}
function useRetryCheckout() {
  const queryClient = useQueryClient();
  return useMutation(
    ({ ...props }: IRetryOrderPayment & { stripe: Stripe }) =>
      handleCheckoutTry({ ...props, type: "retry" }),
    {
      onSettled: (data, props) => {
        queryClient.invalidateQueries("licenses");
        queryClient.invalidateQueries("orders");
      },
    }
  );
}

export {
  useCheckout,
  fetchCheckout,
  useEditCheckout,
  useProcessCheckout,
  useRetryCheckout,
};
